import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import {
  Users,
  TrendingUp,
  Target,
  Clock,
  ArrowRight,
  Briefcase,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Spinner } from '@/components/ui/spinner'
import { StatusBadge } from '@/components/StatusBadge'
import { ScoreBadge } from '@/components/ScoreBadge'
import { SalesFunnelChart } from '@/components/SalesFunnelChart'
import { useFunnelStats } from '@/hooks/useStats'
import { useLeads } from '@/hooks/useLeads'
import { LeadStatus } from '@/types/api'

export function DashboardPage() {
  const navigate = useNavigate()
  const { data: stats, isLoading: statsLoading } = useFunnelStats()
  const { data: recentLeads, isLoading: leadsLoading } = useLeads({
    limit: 10,
    sort_by: 'created_at',
    sort_order: 'desc',
  })

  if (statsLoading || leadsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <Button onClick={() => navigate('/leads/new')}>
          Add New Lead
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-2xl font-bold">{stats?.total_leads || 0}</div>
            <p className="text-xs text-muted-foreground">
              All time leads in system
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-2xl font-bold">{stats?.total_projects || 0}</div>
            <p className="text-xs text-muted-foreground">
              All time projects
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-2xl font-bold">
              {(stats?.conversion_rate || 0).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Leads converted to won
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Won Deals</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-2xl font-bold text-success">
              {stats?.total_projects_won || stats?.total_won || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Successful conversions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Lead Score</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-2xl font-bold">
              {(stats?.avg_lead_score || 0).toFixed(0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Average lead quality score
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Funnel Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Sales Funnel</CardTitle>
          <CardDescription>
            {stats?.count_by_project_status && stats.count_by_project_status.length > 0
              ? 'Project distribution by status'
              : 'Lead distribution by status'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SalesFunnelChart
            countByStatus={stats?.count_by_status ?? []}
            totalLeads={stats?.total_leads ?? 0}
            countByProjectStatus={stats?.count_by_project_status ?? undefined}
            totalProjects={stats?.total_projects ?? undefined}
          />
        </CardContent>
      </Card>

      {/* Recent Leads */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent Leads</CardTitle>
            <CardDescription>Latest 10 leads added to the system</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/leads')}>
            View All
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">Phone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Score</TableHead>
                <TableHead className="hidden md:table-cell">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentLeads?.items.map((lead) => (
                <TableRow
                  key={lead.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/leads/${lead.id}`)}
                >
                  <TableCell className="font-medium">
                    {lead.first_name} {lead.last_name}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{lead.phone || '-'}</TableCell>
                  <TableCell>
                    <StatusBadge status={lead.lead_status as LeadStatus} />
                  </TableCell>
                  <TableCell>
                    <ScoreBadge score={lead.lead_score} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">
                    {format(new Date(lead.created_at), 'MMM d, yyyy')}
                  </TableCell>
                </TableRow>
              ))}
              {(!recentLeads?.items || recentLeads.items.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No leads found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
